/**
 * WebGL capability detection.
 *
 * We probe once per page load and cache the result. Prefers WebGL2, but falls
 * back to WebGL1 since react-globe.gl / three.js can still initialise on WebGL1
 * with degraded quality. If neither can create a context (hardware accel off,
 * headless env, driver crash), we return false and callers force 2D map mode.
 */
let _cached = null;

export function hasWebGLSupport() {
  if (_cached !== null) return _cached;
  try {
    if (typeof window === "undefined" || !window.document) {
      _cached = false;
      return _cached;
    }
    // Bail early on environments known to lack GPU.
    if (window.WebGLRenderingContext === undefined) {
      _cached = false;
      return _cached;
    }
    const canvas = document.createElement("canvas");
    const attempt = (id) => {
      try {
        return canvas.getContext(id, { failIfMajorPerformanceCaveat: false });
      } catch { return null; }
    };
    const ctx =
      attempt("webgl2") ||
      attempt("webgl") ||
      attempt("experimental-webgl");
    if (!ctx) {
      _cached = false;
      return _cached;
    }
    // Some browsers hand back a "software" context that immediately errors on
    // shader compile. Ping the vendor string — if it's unrecoverable, mark unsupported.
    try {
      const dbg = ctx.getExtension?.("WEBGL_debug_renderer_info");
      // If we can't even read parameters, treat as unsupported.
      if (dbg) ctx.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
    } catch {
      _cached = false;
      return _cached;
    }
    _cached = true;
    return _cached;
  } catch {
    _cached = false;
    return _cached;
  }
}

/** Force the cache to false (e.g. after a runtime WebGL crash caught by ErrorBoundary). */
export function markWebGLUnsupported() {
  _cached = false;
}
